/**
 * Shared scaffolding for non-intake typed encounter forms (General,
 * Case-Management, Discharge — and used as the common substrate for the
 * Clinical and CoCM follow-up forms when their additional state is layered
 * in via composition).
 *
 * Owns:
 *   - patient + existing-encounter resolution (props or URL)
 *   - practitioner role lookup + matching role for current profile
 *   - edit-mode hydration through `loadEncounterFormDataContext` +
 *     `encounterToForm` + `fromBundleInput[T]`
 *   - free-creation owner pre-fill
 *   - next-task owner override sync (activity flow)
 *   - common-fields hydration via `useEncounterCommonFields`
 *   - form protection (`useFormProtection` + ref bridge)
 *
 * Each typed form provides its `useForm` instance, its discriminator code,
 * and (optionally) a per-type post-hydration adjustment.
 *
 * @agentic connections
 */

import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { useMedplum, useMedplumProfile, useResource } from "@medplum/react";
import type { UseFormReturnType } from "@mantine/form";
import type {
  Encounter,
  Patient,
  Practitioner,
  PractitionerRole,
} from "@medplum/fhirtypes";

import { PRACTITIONER_ROLE_PAGINATION_COUNT } from "@benchmark/core/constants";
import { CONFIG } from "@/app/config";
import { useFormProtection } from "@benchmark/ui/hooks";
import { useStableParams } from "@/hooks/common/useStableParams";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { usePractitionerRoleOptions } from "@/hooks/shared/useFHIROptions";
import { loadEncounterFormDataContext } from "@benchmark/core/services";
import { encounterToForm } from "@benchmark/core/adapters";
import { fromBundleInput } from "@benchmark/core/adapters";
import type {
  EncounterActivityContext,
  EncounterFormDataByType,
} from "@benchmark/core/types";

import type { EncounterFormRef } from "../EncounterFormShell";
import type { NonIntakeEncounterCode } from "../forms/useEncounterUpsertSubmit";
import { useEncounterCommonFields } from "./useEncounterCommonFields";

/**
 * Inputs to {@link useNonIntakeFormScaffold}.
 *
 * @agentic connections
 */
export type UseNonIntakeFormScaffoldInputs<T extends NonIntakeEncounterCode> = {
  /** The typed form's encounter-type discriminator. */
  type: T;
  /** Mantine `useForm` instance for the typed form. */
  form: UseFormReturnType<EncounterFormDataByType[T]>;
  /** Optional explicit patient ID prop (ModalProvider path). */
  propPatientId?: string;
  /** Optional explicit existing encounter prop (edit/draft resume). */
  propEncounter?: Encounter;
  /** Activity context (single-string mapped or array-mapped). */
  activityContext?: EncounterActivityContext;
  /** Activity completion override owner (pinned by parent). */
  nextTaskOwnerOverride?: { ref: string; display: string } | null;
  /** Forwarded ref from `forwardRef`. */
  ref: React.ForwardedRef<EncounterFormRef>;
};

/**
 * Outputs from {@link useNonIntakeFormScaffold}.
 *
 * @agentic connections
 */
export type UseNonIntakeFormScaffoldOutputs<T extends NonIntakeEncounterCode> =
  {
    patient: Patient | undefined;
    existingEncounter: Encounter | undefined;
    isEditing: boolean;
    isLoadingEdit: boolean;
    loadError: boolean;
    practitionerRoles: PractitionerRole[];
    profile: Practitioner | undefined;
    ConfirmationModal: ReturnType<
      typeof useFormProtection
    >["ConfirmationModal"];
    markAsClean: () => void;
    /** Original form data captured on hydrate, used for diff-aware upsert. */
    originalFormData: EncounterFormDataByType[T] | undefined;
  };

/**
 * Wires up patient + encounter + practitioner-role + form-protection +
 * common-fields hydration for a non-intake typed encounter form.
 *
 * Pulled out of the legacy `Encounter.form.tsx` so each typed form's body
 * stays focused on its UI surface (steps + JSX).
 *
 * @agentic connections
 */
export const useNonIntakeFormScaffold = <T extends NonIntakeEncounterCode>({
  type,
  form,
  propPatientId,
  propEncounter,
  activityContext,
  nextTaskOwnerOverride,
  ref,
}: UseNonIntakeFormScaffoldInputs<T>): UseNonIntakeFormScaffoldOutputs<T> => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const { encounterId, patientId: urlPatientId } = useStableParams();
  const effectivePatientId = propPatientId || urlPatientId;
  const { data: patientResource } = usePatientResource(effectivePatientId);
  const patient = patientResource as Patient | undefined;

  const shouldUseUrlParams =
    propEncounter === undefined && propPatientId === undefined;
  const fetchedEncounter = useResource(
    shouldUseUrlParams && encounterId
      ? { reference: `Encounter/${encounterId}` }
      : undefined,
  );
  const existingEncounter = (propEncounter ?? fetchedEncounter ?? undefined) as
    Encounter | undefined;
  const isEditing = !!existingEncounter;

  const { data: practitionerRoleResources } = usePractitionerRoleOptions({
    organization: `Organization/${CONFIG.VITE_BENCHMARK_ORGANIZATION_ID}`,
    active: "true",
    _include: "PractitionerRole:practitioner",
    _count: PRACTITIONER_ROLE_PAGINATION_COUNT,
  });
  const practitionerRoles = (practitionerRoleResources ??
    []) as PractitionerRole[];

  const { checkProtection, markAsClean, ConfirmationModal } = useFormProtection(
    form,
    {
      enabled: true,
      blockNavigation: true,
      confirmationMessage:
        "You have unsaved changes. Are you sure you want to close without saving?",
    },
  );

  useImperativeHandle(ref, () => ({ checkProtection, markAsClean }), [
    checkProtection,
    markAsClean,
  ]);

  useEncounterCommonFields(form, patient, { isEditing });

  const hydratedRef = useRef(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(isEditing);
  const [loadError, setLoadError] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<
    EncounterFormDataByType[T] | undefined
  >(undefined);
  useEffect(() => {
    if (!isEditing) return;
    if (!patient) return;
    if (!existingEncounter) return;
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    (async () => {
      try {
        const context = await loadEncounterFormDataContext(
          medplum,
          existingEncounter,
          patient,
          { throwOnError: true },
        );
        const flat = encounterToForm(context);
        const typed = fromBundleInput(type, flat);
        form.setValues(typed);
        form.setInitialValues(typed);
        form.resetDirty();
        setOriginalFormData(typed);
      } catch {
        console.error("Failed to hydrate encounter");
        setLoadError(true);
      } finally {
        setIsLoadingEdit(false);
      }
    })();
  }, [isEditing, patient, existingEncounter, medplum, form, type]);

  const ownerPrefilledRef = useRef(false);
  useEffect(() => {
    if (activityContext?.activityCode) return;
    if (!practitionerRoleResources) return;
    if (ownerPrefilledRef.current) return;
    if (
      (form.values as { "followup-assigned-to"?: string })[
        "followup-assigned-to"
      ]
    ) {
      ownerPrefilledRef.current = true;
      return;
    }
    const myRole = practitionerRoles.find(
      (role) => role.practitioner?.reference === `Practitioner/${profile?.id}`,
    );
    if (myRole?.id) {
      form.setFieldValue("followup-assigned-to" as never, myRole.id as never);
      form.setFieldValue(
        "followup-assigned-to-display" as never,
        (myRole.practitioner?.display || "") as never,
      );
      ownerPrefilledRef.current = true;
    }
  }, [
    activityContext?.activityCode,
    practitionerRoles,
    practitionerRoleResources,
    profile?.id,
    form,
  ]);

  useEffect(() => {
    if (!activityContext || !nextTaskOwnerOverride) return;
    form.setFieldValue(
      "followup-assigned-to" as never,
      nextTaskOwnerOverride.ref as never,
    );
    form.setFieldValue(
      "followup-assigned-to-display" as never,
      nextTaskOwnerOverride.display as never,
    );
    // `form` is intentionally omitted: Mantine's `useForm` returns a new object
    // identity each render, so including it here re-runs this effect after the
    // `setFieldValue` re-render, looping until React throws "Maximum update
    // depth exceeded". Re-run only when the override owner actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityContext, nextTaskOwnerOverride]);

  return {
    patient,
    existingEncounter,
    isEditing,
    isLoadingEdit,
    loadError,
    practitionerRoles,
    profile,
    ConfirmationModal,
    markAsClean,
    originalFormData,
  };
};
